// =============================================================================
// app/src/lib/notificacoes.ts — o que o sino tem a dizer.
//
// Pedido do dono (17/08/2026): um símbolo de notificação ao lado do nome, para
// meta individual, meta de equipe e eventos da empresa.
//
// 28/09/2026: os avisos de META são só do dono. "Meta batida", "faltam R$ X" e
// "sua venda já cobre 50% da meta do time" são faturamento em forma de recado,
// e funcionário — vendedor E gerente — não vê faturamento. O resto do sino
// continua igual para quem pode: encomenda, recado, orçamento, fiado, conta.
//
// DERIVADO, NÃO ARMAZENADO (regra 4): não existe tabela de notificações. Cada
// aviso nasce dos dados que já existem (metas_progresso, v_vendas_por_vendedor,
// agenda_do_periodo) e tem um ID estável — o "já vi" é só uma lista de IDs no
// aparelho (localStorage). Sem estado novo no banco, sem job, sem marcação de
// leitura para sincronizar.
//
// A parte pura (montar/naoVistas) é testada; quem busca os dados é o Sino.
// =============================================================================
import type { AgendaOcorrencia, MetaProgressoRow } from '@core/types';

/**
 * As chaves de duas consultas do sino que o MENU também lê (29/10/2026): o
 * número ao lado de "Encomendas" e de "Contas a pagar". O menu não pergunta
 * nada ao banco — ele só olha o que o sino já trouxe, pela MESMA chave. Por
 * isso a chave mora aqui, uma vez só: se o sino a mudasse sozinho, o número do
 * menu sumiria calado.
 *
 * A régua de papel continua a do sino: as contas só são buscadas para o dono
 * (`enabled`), e o item "Contas a pagar" só existe no menu dele.
 */
export const CHAVES_DO_SINO = {
  /** Contas em aberto com vencimento até hoje (atrasadas + vencendo hoje). */
  contas: (hoje: string) => ['sino', 'contas', hoje] as const,
  /** Encomendas ativas que vencem hoje (a RLS entrega ao vendedor só as dele). */
  encomendas: (hoje: string) => ['sino', 'encomendas', hoje] as const,
};

export interface Notificacao {
  /** Estável entre recálculos — é o que o localStorage lembra como "visto". */
  id: string;
  titulo: string;
  detalhe: string | null;
  /** Para onde o toque leva. */
  para: string;
}

const ROTULO_METRICA: Record<string, string> = {
  faturamento: 'faturamento',
  lucro_liquido: 'lucro líquido',
  qtd_vendas: 'quantidade de vendas',
  ticket_medio: 'ticket médio',
};

/**
 * MARCOS da contribuição pessoal: sem meta POR VENDEDOR no modelo (a meta é da
 * loja), o número honesto que existe é "quanto da meta do time a SUA venda já
 * cobre" — o mesmo que a tela Metas mostra. O sino avisa nos quartos.
 */
export const MARCOS_PESSOAIS = [100, 75, 50, 25] as const;

const dataBRDeISO = (iso: string) => iso.split('-').reverse().join('/');
const brlCurto = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

/** Os degraus do fiado antigo: avisa aos 30, de novo aos 60, de novo aos 90. */
export const DEGRAUS_FIADO = [90, 60, 30] as const;

export function montarNotificacoes(p: {
  progresso: MetaProgressoRow[];
  /** Faturamento pessoal no trimestre (soma dos meses), em R$. */
  minhaContribuicao: number;
  agenda: AgendaOcorrencia[];
  hoje: string; // 'YYYY-MM-DD'
  /**
   * Quem está com o aparelho na mão pode ver faturamento? (28/09/2026)
   *
   * Os avisos de META são faturamento em forma de recado — "meta batida",
   * "faltam R$ X", "sua venda já cobre 50% da meta". O dono mandou tirar isso
   * da equipe, então eles só nascem para ele. AUSENTE = NÃO, de propósito:
   * aviso de dinheiro que vaza por esquecimento de parâmetro é o tipo de
   * defeito que ninguém percebe até o sino apitar no balcão.
   */
  verFaturamento?: boolean;
  // --- os operacionais (17/08/2026, escolhidos pelo dono) — todos opcionais:
  // quem não pode ver recebe lista vazia da RLS e nenhum aviso nasce.
  /** Contas em aberto com vencimento ATÉ hoje (dono). */
  contas?: Array<{ id: string; vencimento: string; valor_previsto: number | null; valor_real: number | null }>;
  /** Fiados em aberto (gerência). */
  fiados?: Array<{ id: string; cliente: string; saldo: number; dias_em_aberto: number; perdido_em: string | null }>;
  /** Pedidos com encomenda de cliente chegada e não avisada (gerência). */
  encomendasAvisar?: Array<{ id: string; numero: number; encomendas_a_avisar: number }>;
  /** Orçamentos abertos vencendo hoje/amanhã (todos). */
  orcamentosVencendo?: Array<{ id: string; codigo: string; cliente_nome: string | null; valido_ate: string }>;
  /** Promoções abertas terminando em até 2 dias (gerência). */
  promosTerminando?: Array<{ id: string; fim: string; products: { descricao: string } | null }>;
  /** Recados de encomenda endereçados a mim, ou a todos (050). */
  recadosDeEncomenda?: Array<{
    id: string; cliente_nome: string; aviso_texto: string | null;
    aviso_para: string | null; aviso_de_nome: string | null;
  }>;
  /** Encomendas ATIVAS vencendo hoje (vendedor: as dele; gerência: todas). */
  /** 066: a encomenda é lista, e o aviso diz QUAIS peças — sem isso ele
   *  manda o vendedor abrir a tela só para descobrir do que se trata. */
  encomendasVencendo?: Array<{ id: string; cliente_nome: string; pecas: string[] }>;
  /** Impressões com status de falha (gerência). */
  impressoesFalhas?: Array<{ id: string; titulo: string; erro: string | null }>;
  /** Documentos esperando há mais de 10 min SEM ninguém pegar (gerência): é o
   *  sinal de que o programa de impressão da loja não está rodando. */
  impressoesParadas?: number;
  /** Para onde o aviso de impressão aponta (dono: /sistema; gerente: /a-ver). */
  paraImpressao?: string;
  /** Pedidos do portal B2B aguardando a loja (gerência, migração 062). */
  pedidosB2B?: Array<{ id: string; codigo: string; cliente_nome: string; total: number }>;
  /** Peças que o vendedor cadastrou e esperam liberação (gerência, 093). */
  pecasParaLiberar?: number;
}): Notificacao[] {
  const avisos: Notificacao[] = [];

  // PEDIDO DE PARCEIRO (gerência): primeiro da lista de propósito — é cliente
  // ESPERANDO resposta, e cada um vira um aviso próprio (o id é o pedido: o
  // aviso nasce uma vez e morre quando a loja confirma).
  for (const pb of p.pedidosB2B ?? []) {
    avisos.push({
      id: `b2b-${pb.id}`,
      titulo: `Pedido de parceiro: ${pb.codigo}`,
      detalhe: `${pb.cliente_nome}${Number(pb.total) > 0 ? ` · ${brlCurto(Number(pb.total))}` : ''} — aguardando confirmação`,
      para: '/parceiros',
    });
  }

  // CONTAS (dono): um aviso por DIA, de propósito — atraso lembra todo dia
  // até alguém pagar; o id carrega a data para renascer amanhã.
  const atrasadas = (p.contas ?? []).filter((c) => c.vencimento < p.hoje);
  const vencemHoje = (p.contas ?? []).filter((c) => c.vencimento === p.hoje);
  const soma = (xs: typeof atrasadas) =>
    xs.reduce((s, c) => s + Number(c.valor_real ?? c.valor_previsto ?? 0), 0);
  if (atrasadas.length > 0) {
    avisos.push({
      id: `contas-atrasadas-${p.hoje}`,
      titulo: `${atrasadas.length} conta(s) ATRASADA(S)`,
      detalhe: soma(atrasadas) > 0 ? `${brlCurto(soma(atrasadas))} em atraso` : null,
      para: '/contas',
    });
  }
  if (vencemHoje.length > 0) {
    avisos.push({
      id: `contas-hoje-${p.hoje}`,
      titulo: `Vence hoje: ${vencemHoje.length} conta(s)`,
      detalhe: soma(vencemHoje) > 0 ? brlCurto(soma(vencemHoje)) : null,
      para: '/contas',
    });
  }

  // FIADO ANTIGO (gerência): um aviso por dívida, no degrau que ela cruzou —
  // 30 avisa, 60 avisa DE NOVO, 90 idem. É a fila de cobrança batendo à porta.
  for (const f of p.fiados ?? []) {
    if (f.perdido_em || Number(f.saldo) <= 0) continue;
    const degrau = DEGRAUS_FIADO.find((d) => f.dias_em_aberto >= d);
    if (!degrau) continue;
    avisos.push({
      id: `fiado-${f.id}-${degrau}`,
      titulo: `Fiado de ${f.cliente}: ${f.dias_em_aberto} dias`,
      detalhe: `${brlCurto(Number(f.saldo))} em aberto`,
      para: '/fiado',
    });
  }

  // ENCOMENDA CHEGOU (gerência): cliente esperando aviso é venda esperando
  // fechar. O nº de clientes entra no id: chegou mais um, avisa de novo.
  for (const e of p.encomendasAvisar ?? []) {
    if (e.encomendas_a_avisar <= 0) continue;
    avisos.push({
      id: `avisar-${e.id}-${e.encomendas_a_avisar}`,
      titulo: `Encomenda chegou: ${e.encomendas_a_avisar} cliente(s) a avisar`,
      detalhe: `pedido nº ${e.numero}`,
      para: '/compras',
    });
  }

  // PEÇA ESPERANDO LIBERAÇÃO (gerência, 093): o vendedor cadastrou e ela não
  // vende até alguém liberar — cliente voltando amanhã pela peça e ela ainda
  // travada é venda perdida. O nº entra no id: chegou mais uma, avisa de novo.
  const paraLiberar = p.pecasParaLiberar ?? 0;
  if (paraLiberar > 0) {
    avisos.push({
      id: `liberar-pecas-${paraLiberar}`,
      titulo: `${paraLiberar} peça(s) esperando liberação`,
      detalhe: 'cadastradas no balcão — só vendem depois de liberadas',
      para: '/conferencia',
    });
  }

  // RECADO DE ENCOMENDA (050): o colega avisou para a loja não prometer duas
  // vezes a mesma peça — nem dizer "não temos" a quem já foi prometido.
  for (const r of p.recadosDeEncomenda ?? []) {
    avisos.push({
      id: `recado-${r.id}`,
      titulo: `${r.aviso_de_nome ?? 'Um colega'} avisa: ${r.cliente_nome}`,
      detalhe: [r.aviso_texto, r.aviso_para === null ? '(para todos)' : null]
        .filter(Boolean).join(' · ') || null,
      para: '/encomendas',
    });
  }

  // ENCOMENDA VENCENDO (18/08/2026): a peça separada expira HOJE — ligar para
  // o cliente antes de a reserva soltar. Vendedor recebe as dele; gerência,
  // todas — o recorte é da RLS, o aviso é o mesmo.
  for (const e of p.encomendasVencendo ?? []) {
    avisos.push({
      id: `encomenda-vence-${e.id}`,
      titulo: `Encomenda vence HOJE: ${e.cliente_nome}`,
      // Duas peças cabem na linha do sino; da terceira em diante vira contagem,
      // senão um pedido grande empurra os outros avisos para fora da tela.
      detalhe: e.pecas.length === 0 ? null
        : e.pecas.length <= 2 ? e.pecas.join(' · ')
          : `${e.pecas[0]} + ${e.pecas.length - 1} peça(s)`,
      para: '/encomendas',
    });
  }

  // ORÇAMENTO VENCENDO (todos): a chance de ligar antes de expirar.
  for (const o of p.orcamentosVencendo ?? []) {
    avisos.push({
      id: `orc-vence-${o.id}`,
      titulo: `Orçamento ${o.codigo} vence ${o.valido_ate === p.hoje ? 'HOJE' : 'amanhã'}`,
      detalhe: o.cliente_nome ?? null,
      para: `/orcamentos/${o.id}`,
    });
  }

  // PROMOÇÃO TERMINANDO (gerência): renova ou deixa morrer — decisão com data.
  for (const pr of p.promosTerminando ?? []) {
    avisos.push({
      id: `promo-fim-${pr.id}`,
      titulo: `Promoção termina ${pr.fim === p.hoje ? 'HOJE' : `em ${dataBRDeISO(pr.fim)}`}`,
      detalhe: pr.products?.descricao ?? null,
      para: '/promocoes',
    });
  }

  // NINGUÉM PEGOU O PAPEL (19/08/2026): documento esperando com zero tentativa
  // é o retrato do agente fora do ar — a loja ficou 12 dias assim, e o que
  // faltou foi exatamente este aviso. O id carrega a data: volta a avisar
  // amanhã se continuar parado.
  if ((p.impressoesParadas ?? 0) > 0) {
    avisos.push({
      id: `print-parado-${p.hoje}`,
      titulo: `${p.impressoesParadas} documento(s) esperando para imprimir`,
      detalhe: 'ninguém pegou — o programa de impressão da loja pode estar fora do ar',
      para: p.paraImpressao ?? '/impressoes',
    });
  }

  // IMPRESSÃO FALHOU (gerência): 3 tentativas esgotadas — sem aviso, o papel
  // simplesmente não sai e ninguém descobre.
  for (const j of p.impressoesFalhas ?? []) {
    avisos.push({
      id: `print-falha-${j.id}`,
      titulo: `Impressão falhou: ${j.titulo}`,
      detalhe: j.erro,
      para: p.paraImpressao ?? '/a-ver',
    });
  }

  // Meta de EQUIPE batida — uma por métrica atingida. Só para quem pode ver
  // faturamento (o dono): para a equipe, a tela de metas não existe mais, e
  // aviso que leva a uma tela fechada é só frustração.
  for (const m of p.verFaturamento ? p.progresso : []) {
    if (Number(m.atingido_pct) >= 100) {
      avisos.push({
        id: `meta-equipe-${m.goal_id}`,
        titulo: `Meta de ${ROTULO_METRICA[m.metrica] ?? m.metrica} do trimestre batida!`,
        detalhe: `${Math.round(Number(m.atingido_pct))}% do alvo`,
        para: '/metas',
      });
    }
  }

  // A SUA parte — marcos de 25% sobre a meta de faturamento do time. O maior
  // marco alcançado é o aviso; os anteriores já foram vistos no caminho.
  // Também só para quem vê faturamento: "sua venda já cobre 50% da meta" é
  // dizer, em outras palavras, quanto a loja vende e quanto a pessoa vendeu.
  const fat = p.verFaturamento
    ? p.progresso.find((m) => m.metrica === 'faturamento') : undefined;
  if (fat && Number(fat.valor_meta) > 0 && p.minhaContribuicao > 0) {
    const pct = (p.minhaContribuicao / Number(fat.valor_meta)) * 100;
    const marco = MARCOS_PESSOAIS.find((x) => pct >= x);
    if (marco) {
      avisos.push({
        id: `minha-parte-${fat.goal_id}-${marco}`,
        titulo: marco >= 100
          ? 'Sua venda cobriu a meta do time inteira!'
          : `Sua venda já cobre ${marco}% da meta do time`,
        detalhe: null,
        para: '/metas',
      });
    }
  }

  // Eventos da EMPRESA (escopo loja/conjunta) de hoje e de amanhã, ainda não
  // concluídos. O pessoal fica de fora: a agenda própria a pessoa já olha.
  for (const a of p.agenda) {
    if (a.concluida_em) continue;
    if (a.escopo !== 'loja' && a.escopo !== 'conjunta') continue;
    avisos.push({
      id: `agenda-${a.tarefa_id}-${a.dia}`,
      titulo: `${a.dia === p.hoje ? 'Hoje' : 'Amanhã'}: ${a.titulo}`,
      detalhe: a.hora ? `às ${a.hora.slice(0, 5)}` : null,
      para: '/agenda',
    });
  }

  return avisos;
}

// naoVistas/podarVistas (v1) morreram em 18/08/2026: o registro abaixo os
// substitui — o "visto" virou estado POR AVISO, com conferir/ocultar e uma
// semana de histórico.

// ---------------------------------------------------------------------------
// O REGISTRO (18/08/2026): cada aviso ganha estado no APARELHO — lida (o
// painel abriu com ele à vista), conferida (a pessoa deu conta: ✓) e ocultada
// (✕, some de vez). E ganha memória: aviso que saiu do ar fica visível por
// UMA SEMANA como histórico, a não ser que a pessoa oculte.
//
// Continua tudo derivado + localStorage (regra 4): o banco não sabe de leitura
// de notificação, e trocar de aparelho zera o registro, nunca os dados.

export interface RegistroNotificacao extends Notificacao {
  /** O dia em que o aviso apareceu pela primeira vez ('YYYY-MM-DD'). */
  visto_em: string;
  lida: boolean;
  conferida: boolean;
  ocultada: boolean;
}

/** Avisos agregados que se refazem por dia (o id carrega a data): quando saem
 *  do ar, saem do histórico junto — o "3 contas atrasadas" de ontem ao lado do
 *  de hoje seria ruído, não memória. */
const PREFIXOS_EFEMEROS = ['contas-atrasadas-', 'contas-hoje-'];

export function dentroDaSemana(vistoEm: string, hoje: string): boolean {
  const dias = Math.round(
    (new Date(`${hoje}T12:00:00`).getTime() - new Date(`${vistoEm}T12:00:00`).getTime())
    / 86_400_000,
  );
  return dias >= 0 && dias < 7;
}

/** Junta o que está NO AR com o que o aparelho lembra. Idempotente. */
export function mesclarRegistro(
  atuais: Notificacao[],
  registro: RegistroNotificacao[],
  hoje: string,
): RegistroNotificacao[] {
  const lembrado = new Map(registro.map((r) => [r.id, r]));
  const noAr = new Set(atuais.map((n) => n.id));

  const mesclado: RegistroNotificacao[] = atuais.map((n) => {
    const velho = lembrado.get(n.id);
    // Texto sempre o de AGORA (o detalhe muda: dias do fiado, soma das
    // contas); o estado (lida/conferida/ocultada) é o que o aparelho lembra.
    return velho
      ? { ...velho, titulo: n.titulo, detalhe: n.detalhe, para: n.para }
      : { ...n, visto_em: hoje, lida: false, conferida: false, ocultada: false };
  });

  for (const r of registro) {
    if (noAr.has(r.id)) continue;
    if (PREFIXOS_EFEMEROS.some((p) => r.id.startsWith(p))) continue;
    if (!dentroDaSemana(r.visto_em, hoje)) continue;
    mesclado.push(r);
  }
  return mesclado;
}

/** O painel não mostra o que a pessoa ocultou. Não lidas primeiro; depois o
 *  histórico, mais novo em cima. */
export function paraExibir(registro: RegistroNotificacao[]): RegistroNotificacao[] {
  return registro
    .filter((r) => !r.ocultada)
    .sort((a, b) => (Number(a.lida) - Number(b.lida)) || b.visto_em.localeCompare(a.visto_em));
}

/** O número do badge: o que ainda não foi visto (nem escondido). */
export function contarNovas(registro: RegistroNotificacao[]): number {
  return registro.filter((r) => !r.lida && !r.ocultada).length;
}

/** Abrir o painel LÊ tudo que está à vista — é o "blur" pedido pelo dono. */
export function marcarLidas(registro: RegistroNotificacao[]): RegistroNotificacao[] {
  return registro.map((r) => (r.ocultada ? r : { ...r, lida: true }));
}

/** O ✓: "dei conta disso". Alterna — desmarcar também é gesto legítimo. */
export function alternarConferida(
  registro: RegistroNotificacao[], id: string,
): RegistroNotificacao[] {
  return registro.map((r) =>
    r.id === id ? { ...r, conferida: !r.conferida, lida: true } : r);
}

/** O ✕: some de vez (e não volta enquanto o aviso existir — o id fica). */
export function ocultarNotificacao(
  registro: RegistroNotificacao[], id: string,
): RegistroNotificacao[] {
  return registro.map((r) =>
    r.id === id ? { ...r, ocultada: true, lida: true } : r);
}
